import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { CalendarClock, X } from "lucide-react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import StatusBadge from "./StatusBadge";
import { USE_MOCK_AUTH } from "../../config/api";
import {
  createCasePaymentPlan,
  fetchOpsPaymentPlans,
  fetchOpsPreApprovals,
  updateOpsPaymentPlan,
} from "../../services/paymentApi";
import { formatCurrency } from "../../data/mock/dashboardData";
import { showError, showSuccess } from "../../utils/toast";

const PAYMENT_METHODS = [
  "Mobile Money",
  // "Bank transfer",
  // "Bank branch",
  // "Cash at partner agent",
];

const WEEKDAYS = [
  { id: 1, label: "Mon" },
  { id: 2, label: "Tue" },
  { id: 3, label: "Wed" },
  { id: 4, label: "Thu" },
  { id: 5, label: "Fri" },
  { id: 6, label: "Sat" },
  { id: 7, label: "Sun" },
];

function formatWhen(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

function weekdayLabel(days) {
  const selected = Array.isArray(days) ? days.map(Number) : [];
  const labels = WEEKDAYS.filter((day) => selected.includes(day.id)).map((day) => day.label);
  return labels.length ? labels.join(", ") : "—";
}

function scheduleLabel(row) {
  if (row?.frequency === "monthly") {
    return row.date_in_month ? `Day ${row.date_in_month} of the month` : "Monthly";
  }
  return weekdayLabel(row?.weekdays || row?.billing_weekdays);
}

function planFromPayload(payload) {
  return payload?.plan || payload || null;
}

function splitInstalments(total, count) {
  const pesewas = Math.round(Number(total) * 100);
  const instalments = Number(count);
  if (!Number.isFinite(pesewas) || pesewas <= 0 || !Number.isFinite(instalments) || instalments < 2) {
    return null;
  }
  const base = Math.floor(pesewas / instalments);
  const remainder = pesewas - base * instalments;
  return {
    regular: (base / 100).toFixed(2),
    last: ((base + remainder) / 100).toFixed(2),
    even: remainder === 0,
    count: instalments,
  };
}

function dateInputValue(value) {
  if (!value) return "";
  const text = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : "";
}

function isApprovedMandate(row) {
  const key = String(row?.approval_status || row?.status || "").toLowerCase();
  return key === "approved" || key === "active";
}

export default function PaymentPlanModal({ caseRow, record = null, onClose, onComplete }) {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [frequency, setFrequency] = useState("weekly");
  const [weekdays, setWeekdays] = useState([1]);
  const [dateInMonth, setDateInMonth] = useState("1");
  const [planTotal, setPlanTotal] = useState("");
  const [instalments, setInstalments] = useState("");
  const [debitAmount, setDebitAmount] = useState("");
  const [firstDueDate, setFirstDueDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Mobile Money");
  const [endDate, setEndDate] = useState("");
  const [attemptsPerDay, setAttemptsPerDay] = useState("3");
  const [preApprovalId, setPreApprovalId] = useState("");
  const [mandates, setMandates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [result, setResult] = useState(null);

  const loadExisting = async ({ force = false } = {}) => {
    if (!useLiveApi || !caseRow?.id) {
      setExisting([]);
      setMandates([]);
      return;
    }
    setLoadingExisting(true);
    try {
      const [rows, approvals] = await Promise.all([
        fetchOpsPaymentPlans(token, { case_id: caseRow.id }, { force }),
        fetchOpsPreApprovals(token, { case_id: caseRow.id }, { force }),
      ]);
      setExisting(Array.isArray(rows) ? rows : []);
      setMandates(Array.isArray(approvals) ? approvals : []);
    } catch {
      setExisting([]);
      setMandates([]);
    } finally {
      setLoadingExisting(false);
    }
  };

  useEffect(() => {
    if (!caseRow && !record) return;
    const savedDays = Array.isArray(record?.weekdays)
      ? record.weekdays.map(Number)
      : Array.isArray(record?.billing_weekdays)
        ? record.billing_weekdays.map(Number)
        : [1];
    setFrequency(record?.frequency === "monthly" ? "monthly" : "weekly");
    setWeekdays(savedDays.length ? savedDays : [1]);
    setDateInMonth(record?.date_in_month ? String(record.date_in_month) : "1");
    const owed = Number(caseRow?.amountOwed ?? record?.amountOwed ?? 0);
    const paid = Number(caseRow?.amountPaid ?? record?.amountPaid ?? 0);
    const reported = Number(caseRow?.outstandingAmount ?? caseRow?.outstanding ?? record?.outstandingAmount);
    const derived = Math.max(0, owed - paid);
    const outstanding =
      Number.isFinite(reported) && reported > 0 && paid > 0 && owed > paid && reported >= owed
        ? derived
        : Number.isFinite(reported) && reported >= 0
          ? reported
          : derived;
    setPlanTotal(
      record?.plan_total != null && record.plan_total !== ""
        ? String(record.plan_total)
        : outstanding > 0
          ? String(outstanding)
          : ""
    );
    setInstalments(record?.instalment_count != null ? String(record.instalment_count) : "");
    setDebitAmount(
      record?.debit_amount != null
        ? String(record.debit_amount)
        : record?.instalmentAmount != null
          ? String(record.instalmentAmount)
          : ""
    );
    setFirstDueDate(dateInputValue(record?.first_due_date || record?.plan_start_date || record?.start_date));
    setPaymentMethod(record?.payment_method || "Mobile Money");
    setEndDate(dateInputValue(record?.plan_end_date || record?.end_date));
    setAttemptsPerDay(record?.attempts_per_day != null ? String(record.attempts_per_day) : "3");
    setPreApprovalId(record?.pre_approval_id != null ? String(record.pre_approval_id) : "");
    setResult(null);
    loadExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseRow, record, token, useLiveApi]);

  const instalmentSplit = useMemo(
    () => splitInstalments(planTotal, instalments),
    [planTotal, instalments]
  );

  const approvedMandates = useMemo(
    () => (Array.isArray(mandates) ? mandates.filter(isApprovedMandate) : []),
    [mandates]
  );

  useEffect(() => {
    if (preApprovalId || approvedMandates.length === 0) return;
    if (paymentMethod.toLowerCase().includes("mobile")) {
      setPreApprovalId(String(approvedMandates[0].id));
    }
  }, [approvedMandates, paymentMethod, preApprovalId]);

  useEffect(() => {
    if (!instalmentSplit) return;
    setDebitAmount(instalmentSplit.regular);
  }, [instalmentSplit]);

  if (!caseRow && !record) return null;

  const subject = caseRow || {
    customer: record.customer_name || record.customer,
    accountNumber: record.account_number || record.accountNumber,
    outstandingAmount: record.amountOwed,
  };

  const toggleWeekday = (id) => {
    setWeekdays((current) =>
      current.includes(id) ? current.filter((day) => day !== id) : [...current, id].sort((a, b) => a - b)
    );
  };

  const handleSubmit = async () => {
    const parsedTotal = Number(planTotal);
    const parsedCount = Number(instalments);
    const parsedDebit = Number(debitAmount);
    const parsedAttempts = Number(attemptsPerDay);
    if (!parsedTotal || parsedTotal <= 0) {
      showError("Enter the plan total.");
      return;
    }
    if (!parsedCount || parsedCount < 2) {
      showError("Enter the number of instalments (at least 2).");
      return;
    }
    if (!parsedDebit || parsedDebit <= 0) {
      showError("Enter the debit amount.");
      return;
    }
    if (frequency === "weekly" && weekdays.length === 0) {
      showError("Choose at least one weekday.");
      return;
    }
    if (frequency === "monthly" && (!dateInMonth || Number(dateInMonth) < 1 || Number(dateInMonth) > 28)) {
      showError("Enter the day of the month (1–28).");
      return;
    }
    if (!firstDueDate) {
      showError("Enter the first due date.");
      return;
    }
    if (!paymentMethod) {
      showError("Select a payment method.");
      return;
    }
    if (paymentMethod.toLowerCase().includes("mobile") && !preApprovalId) {
      showError(
        "Link an approved mandate first (Payments → Send mandate, then refresh status after the customer approves on their phone)."
      );
      return;
    }
    setSaving(true);
    try {
      let plan = null;
      const body = {
          frequency,
          weekdays: frequency === "weekly" ? weekdays : undefined,
          date_in_month: frequency === "monthly" ? Number(dateInMonth) : undefined,
          plan_total: parsedTotal,
          instalment_count: parsedCount,
          debit_amount: parsedDebit,
          instalment_amount: parsedDebit,
          first_due_date: firstDueDate,
          payment_method: paymentMethod,
          plan_start_date: firstDueDate,
          plan_end_date: endDate || undefined,
          attempts_per_day: parsedAttempts || 3,
          pre_approval_id: preApprovalId ? Number(preApprovalId) : null,
        };
      if (useLiveApi && record?.id) {
        const payload = await updateOpsPaymentPlan(token, record.numericId || record.id, body);
        plan = planFromPayload(payload);
        setResult(plan);
        await loadExisting({ force: true });
      } else if (useLiveApi) {
        const payload = await createCasePaymentPlan(token, caseRow.id, body);
        plan = planFromPayload(payload);
        setResult(plan);
        await loadExisting({ force: true });
      } else {
        plan = {
          id: "PLN-MOCK",
          status: "active",
          frequency,
          weekdays,
          date_in_month: frequency === "monthly" ? Number(dateInMonth) : null,
          plan_total: parsedTotal,
          instalment_count: parsedCount,
          debit_amount: parsedDebit,
          instalmentAmount: parsedDebit,
          first_due_date: firstDueDate,
          payment_method: paymentMethod,
          plan_start_date: firstDueDate,
          plan_end_date: endDate,
        };
        setResult(plan);
      }
      showSuccess(
        record?.id
          ? `Payment plan updated.`
          : `Payment plan of ${formatCurrency(parsedTotal)} saved.`
      );
      onComplete?.(plan);
    } catch (error) {
      showError(error.message || "Could not create the payment plan.");
    } finally {
      setSaving(false);
    }
  };

  const latest = existing[0] || result;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-plan-title"
        className="flex max-h-[96vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between border-b border-primary/[0.06] px-5 py-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
              Payment plan
            </p>
            <h2 id="payment-plan-title" className="mt-1 text-lg font-bold text-primary">{subject.customer}</h2>
            <p className="text-sm text-primary/50">
              Outstanding {formatCurrency(Number(subject.outstandingAmount ?? subject.amountOwed ?? 0))}
              {subject.accountNumber ? ` · ${subject.accountNumber}` : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-primary/40 hover:bg-primary/5">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {latest ? (
            <div className="rounded-xl border border-primary/10 bg-primary/[0.03] px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
                  Current plan
                </p>
                <StatusBadge status={latest.status} />
              </div>
              <p className="mt-1 text-sm font-semibold text-primary">
                Plan {latest.id} · {formatCurrency(latest.debit_amount || latest.instalmentAmount || 0)} debit
              </p>
              <p className="mt-1 text-xs text-primary/55">
                {latest.frequency || "weekly"} · {scheduleLabel(latest)} · {latest.instalment_count || "—"} instalments
                of {formatCurrency(latest.plan_total || latest.totalAmount || 0)} total
                {latest.payment_method ? ` · ${latest.payment_method}` : ""}
              </p>
            </div>
          ) : null}

          <div>
            <p className="mb-2 text-xs font-semibold text-primary/50">Frequency</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "weekly", label: "Weekly" },
                { id: "monthly", label: "Monthly" },
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setFrequency(item.id)}
                  className={`rounded-xl border px-3 py-2 text-sm font-semibold ${
                    frequency === item.id ? "border-accent/40 bg-accent/[0.08]" : "border-primary/10"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {frequency === "weekly" ? (
            <div>
              <p className="mb-2 text-xs font-semibold text-primary/50">Weekdays</p>
              <div className="grid grid-cols-7 gap-1.5">
                {WEEKDAYS.map((day) => (
                  <button
                    key={day.id}
                    type="button"
                    onClick={() => toggleWeekday(day.id)}
                    className={`rounded-lg border px-1 py-2 text-xs font-semibold ${
                      weekdays.includes(day.id) ? "border-accent/40 bg-accent/[0.08]" : "border-primary/10"
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-primary/50">
                Weekly means each selected day is a collection day. Monday, Wednesday and Friday
                sends the instalment three times a week until the plan total is collected or the end
                date passes. The instalment amount is the plan total split across the number of
                instalments, and you can still change it.
              </p>
            </div>
          ) : (
            <Input
              label="Day of month"
              type="number"
              min="1"
              max="28"
              value={dateInMonth}
              onChange={(e) => setDateInMonth(e.target.value)}
            />
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Plan total (GHS)"
              type="number"
              min="0"
              step="0.01"
              value={planTotal}
              onChange={(e) => setPlanTotal(e.target.value)}
            />
            <Input
              label="Number of instalments"
              type="number"
              min="2"
              value={instalments}
              onChange={(e) => setInstalments(e.target.value)}
            />
            <div>
              <Input
                label="Debit amount (GHS)"
                type="number"
                min="0"
                step="0.01"
                value={debitAmount}
                onChange={(e) => setDebitAmount(e.target.value)}
              />
              {instalmentSplit ? (
                <p className="mt-1 text-[11px] text-primary/45">
                  {instalmentSplit.even
                    ? `${formatCurrency(instalmentSplit.regular)} each instalment.`
                    : `${formatCurrency(instalmentSplit.regular)} for the first ${instalmentSplit.count - 1}. The last instalment is ${formatCurrency(instalmentSplit.last)}.`}
                </p>
              ) : (
                <p className="mt-1 text-[11px] text-primary/45">
                  Filled from the plan total and the number of instalments. You can still change it.
                </p>
              )}
            </div>
            <Input
              label="First due date"
              type="date"
              value={firstDueDate}
              onChange={(e) => setFirstDueDate(e.target.value)}
            />
            <Input
              label="Plan end date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <Input
              label="Attempts per day"
              type="number"
              min="1"
              max="10"
              value={attemptsPerDay}
              onChange={(e) => setAttemptsPerDay(e.target.value)}
            />
            <label className="block text-sm">
              <span className="mb-1.5 block text-xs font-semibold text-primary/50">Payment method</span>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full rounded-xl border border-primary/10 bg-white px-4 py-3 text-sm text-primary outline-none focus:border-accent focus:ring-4 focus:ring-accent/20"
              >
                <option value="">Select method</option>
                {PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="mb-1.5 block text-xs font-semibold text-primary/50">Approved mandate</span>
              <select
                value={preApprovalId}
                onChange={(e) => setPreApprovalId(e.target.value)}
                className="w-full rounded-xl border border-primary/10 bg-white px-4 py-3 text-sm text-primary outline-none focus:border-accent focus:ring-4 focus:ring-accent/20"
              >
                <option value="">Select approved mandate</option>
                {approvedMandates.map((row) => (
                  <option key={row.id || row.reference_id} value={row.id}>
                    {row.reference_id} · {row.network || "MTN"} · {row.msisdn || "wallet"}
                  </option>
                ))}
              </select>
              {paymentMethod.toLowerCase().includes("mobile") && approvedMandates.length === 0 ? (
                <p className="mt-1.5 text-[11px] leading-relaxed text-amber-800">
                  No approved mandate on this case yet. Send one from Log contact (e.g. 0556906969 on MTN), then use
                  Check status on Payments → Mandates after the customer approves.
                </p>
              ) : null}
            </label>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold text-primary/50">Plans already set</p>
              {loadingExisting ? <span className="text-[11px] text-primary/40">Loading…</span> : null}
            </div>
            {existing.length === 0 ? (
              <p className="rounded-xl border border-dashed border-primary/10 px-3 py-4 text-xs text-primary/45">
                No payment plan has been set for this case yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {existing.map((row) => (
                  <li key={row.id} className="rounded-xl border border-primary/10 px-3 py-2.5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold text-primary">
                          Plan {row.id} · {formatCurrency(row.debit_amount || row.instalmentAmount || 0)}
                        </p>
                        <p className="mt-0.5 text-[11px] text-primary/50">
                          {row.frequency || "weekly"} · {scheduleLabel(row)} · {row.instalment_count || "—"} × total{" "}
                          {formatCurrency(row.plan_total || 0)} · {row.payment_method || "—"}
                        </p>
                        <p className="mt-0.5 text-[11px] text-primary/45">
                          {formatWhen(row.plan_start_date || row.start_date)} → {formatWhen(row.plan_end_date || row.end_date)}
                          {row.pre_approval_reference ? ` · mandate ${row.pre_approval_reference}` : ""}
                        </p>
                      </div>
                      <StatusBadge status={row.status} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t border-primary/[0.06] bg-primary/[0.02] px-5 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Close
          </Button>
          <Button size="sm" onClick={handleSubmit} loading={saving}>
            <CalendarClock className="h-4 w-4" />
            {record?.id ? "Save changes" : "Save plan"}
          </Button>
        </div>
      </div>
    </div>
  );
}
